import oDayJs from "dayjs";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import {
  apiCallChecksDelete,
  apiCallChecksSave,
  apiCallChecksUpdate,
  apiCallGetAllChecks,
  apiCallGetBanks,
} from "../api/ChecksServices";
import {
  actionManageMessage,
  actionShowLoading,
  actionDismissLoading,
  actionMessage,
} from "redux/shared/SharedSlice";
import { BanksMapper, ChecksMapper, ChecksUnMapper } from "./ChecksMapper";

import { DATES_FORMATS } from "config/constants/Dates";
import { CONSTANTS_MESSAGE } from "config/constants/Message";

const oInitialState = {
  aChecks: [],
  nRecords: null,
  bStatus: false,
  aBanks: [],

  oCheck: null,

  nPages: null,
  nPage: 1,
  oType: null,
  oDateType: null,
  dDateTo: oDayJs().toString(),
  dDateFrom: oDayJs().toString(),
};

export const actionChecksGetAll = createAsyncThunk("checks/getAll", async(args, oThunk) => {
  try {
    const { ChecksSlice: oState } = oThunk.getState();
    oThunk.dispatch(actionShowLoading());
    const oData = {
      pagina: oState.nPage,
      fchhasta: oDayJs(oState.dDateTo).format(DATES_FORMATS.MYSQL_SERVER),
      fchdesde: oDayJs(oState.dDateFrom).format(DATES_FORMATS.MYSQL_SERVER),
      chetipo: oState.oType?.value || "R",
      tipofecha: oState.oDateType?.value || 1,
    };
    const oSuccess = await apiCallGetAllChecks(oData);
    return { data: ChecksMapper(oSuccess.data), pages: oSuccess.pages, records: oSuccess.records };
  } catch (oError) {
    oThunk.dispatch(actionManageMessage(oError));
    oThunk(oError);
  } finally {
    oThunk.dispatch(actionDismissLoading());
  }
});

export const actionChecksSave = createAsyncThunk("checks/save", async(oData, oThunk) => {
  try {
    oThunk.dispatch(actionShowLoading());
    const oSuccess = await apiCallChecksSave(ChecksUnMapper(oData));
    oThunk.dispatch(
      actionMessage({
        message: oSuccess.message || "Cheque generado correctamente",
        type: CONSTANTS_MESSAGE.TYPE_SUCCESS,
      }),
    );
    return true;
  } catch (oError) {
    oThunk.dispatch(actionManageMessage(oError));
    oThunk(oError);
  } finally {
    oThunk.dispatch(actionDismissLoading());
  }
});

export const actionChecksUpdate = createAsyncThunk("checks/update", async(oData, oThunk) => {
  try {
    oThunk.dispatch(actionShowLoading());
    const oSuccess = await apiCallChecksUpdate(oData.id, ChecksUnMapper(oData));
    oThunk.dispatch(
      actionMessage({
        message: oSuccess.message || "Cheque actualizado correctamente",
        type: CONSTANTS_MESSAGE.TYPE_SUCCESS,
      }),
    );
    return true;
  } catch (oError) {
    oThunk.dispatch(actionManageMessage(oError));
    oThunk(oError);
  } finally {
    oThunk.dispatch(actionDismissLoading());
  }
});

export const actionChecksDelete = createAsyncThunk("checks/delete", async(nId, oThunk) => {
  try {
    oThunk.dispatch(actionShowLoading());
    const oSuccess = await apiCallChecksDelete(nId);
    oThunk.dispatch(actionDismissLoading());
    oThunk.dispatch(
      actionMessage({
        message: oSuccess.message || "Cheque eliminado correctamente",
        type: CONSTANTS_MESSAGE.TYPE_SUCCESS,
      }),
    );
    oThunk.dispatch(actionChecksGetAll());
  } catch (oError) {
    oThunk.dispatch(actionManageMessage(oError));
    oThunk(oError);
  }
});

export const actionChecksGetBanks = createAsyncThunk("checks/get-banks", async(_, oThunk) => {
  try {
    const oSuccess = await apiCallGetBanks();
    return BanksMapper(oSuccess.data);
  } catch (oError) {
    oThunk.dispatch(actionManageMessage(oError));
    oThunk(oError);
  }
});

const ChecksSlice = createSlice({
  name: "ChecksSlice",
  initialState: oInitialState,
  reducers: {
    actionChecksClean: oState => {
      oState.bStatus = false;
      oState.aChecks = [];
      oState.nRecords = false;
      oState.oCheck = null;
      oState.nPage = 1;
    },
    actionChecksChangePage: (oState, oAction) => {
      oState.nPage = oAction.payload;
    },
    actionChecksChangeDateTo: (oState, oAction) => {
      oState.dDateTo = oAction.payload;
    },
    actionChecksChangeDateFrom: (oState, oAction) => {
      oState.dDateFrom = oAction.payload;
    },
    actionChecksChangeType: (oState, oAction) => {
      oState.oType = oAction.payload;
    },
    actionChecksChangeDateType: (oState, oAction) => {
      oState.oDateType = oAction.payload;
    },
    actionChecksCleanStatus: oState => {
      oState.bStatus = false;
    },
    actionChecksCleanFilters: oState => {
      oState.nPage = 1;
      oState.oType = null;
      oState.nPages = null;
      oState.oExpirationDate = null;
      oState.dDateTo = oDayJs().toString();
      oState.dDateFrom = oDayJs().toString();
    },
    actionChecksFind: (oState, oAction) => {
      oState.oCheck = oAction.payload;
    },
  },
  extraReducers: builder => {
    builder.addCase(actionChecksGetAll.fulfilled, (oState, oAction) => {
      oState.aChecks = oAction.payload.data;
      oState.nPages = oAction.payload.pages;
      oState.nRecords = oAction.payload.records;
    });
    builder.addCase(actionChecksGetBanks.fulfilled, (oState, oAction) => {
      oState.aBanks = oAction.payload;
    });
    builder.addCase(actionChecksSave.fulfilled, (oState, oAction) => {
      oState.bStatus = oAction.payload;
    });
    builder.addCase(actionChecksUpdate.fulfilled, (oState, oAction) => {
      oState.bStatus = oAction.payload;
    });
  },
});

export const {
  actionChecksClean,
  actionChecksChangePage,
  actionChecksCleanStatus,
  actionChecksCleanFilters,
  actionChecksChangeStatus,
  actionChecksChangeDateTo,
  actionChecksChangeDateFrom,
  actionChecksChangeType,
  actionChecksChangeDateType,
  actionChecksFind,
} = ChecksSlice.actions;

export const selectChecksState = ({ ChecksSlice: oState }) => oState;

export default ChecksSlice.reducer;
